import React, { useState, useEffect } from "react";
import { X, Calendar, Plus, Upload, Save, Loader } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const StockUpdateForm = ({
  onClose,
  materialId,
  currentStock,
  onStockUpdate,
  materialDetails,
  editMode = false,
  stockData = {},
}) => {
  const alert = useAlert();
  const [formData, setFormData] = useState({
    update_date: new Date().toISOString().split("T")[0],
    entered_quantity: "",
    comment: "",
    material: materialId,
  });

  // Calculate initial stock action based on quantity (for edit mode)
  const initialStockAction = stockData?.entered_quantity > 0 ? "added" : "used";
  const [stockAction, setStockAction] = useState(initialStockAction);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && stockData) {
      setFormData({
        update_date:
          stockData.update_date || new Date().toISOString().split("T")[0],
        entered_quantity: Math.abs(stockData.entered_quantity) || "",
        comment: stockData.comment || "",
        material: materialId,
      });

      // Set stock action based on the sign of entered_quantity
      setStockAction(stockData.entered_quantity > 0 ? "added" : "used");
    }
  }, [editMode, stockData, materialId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      update_date: value,
    }));
    setShowDatePicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem("access");
      const finalQuantity =
        stockAction === "added"
          ? Number(formData.entered_quantity)
          : -Number(formData.entered_quantity);

      if (editMode) {
        // Update existing stock

        const response = await fetch(
          `${BASE_URL}/inventory/update-stock/${stockData.id}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken.trim()}`,
            },
            body: JSON.stringify({
              ...formData,
              entered_quantity: finalQuantity,
            }),
          }
        );

        if (response.ok) {
          const updatedStockData = {
            ...stockData,
            ...formData,
            entered_quantity: finalQuantity,
          };
          // onStockUpdate(updatedStockData);
          alert.addAlert("Stock updated successfully", "success");
          onClose();
        } else {
          const errorData = await response.json();
          alert.addAlert(
            `Failed to update stock: ${errorData.message || "Unknown error"}`,
            "error"
          );
        }
      } else {
        // Create new stock
        const response = await fetch(`${BASE_URL}/inventory/create-stock/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken.trim()}`,
          },
          body: JSON.stringify({
            ...formData,
            entered_quantity: finalQuantity,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          onStockUpdate({
            ...data,
            entered_quantity: finalQuantity,
            created_on: new Date().toISOString(),
          });
          alert.addAlert("Stock added successfully", "success");
          onClose();
        } else {
          const errorData = await response.json();
          alert.addAlert(
            `Failed to create stock: ${errorData.message || "Unknown error"}`,
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Error handling stock:", error);
      alert.addAlert(`Error: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

const validateQuantity = (quantity) => {
  const q = Number(quantity);

  // ❌ bail if nothing or zero/negative
  if ( q < 0) return false;          // <-- NEW line

  // for “used” we must still respect available stock
  if (stockAction === "used") {
    if (editMode && stockData.entered_quantity < 0) {
      const availableStock = currentStock + Math.abs(stockData.entered_quantity);
      return q <= availableStock;
    }
    return q <= currentStock;
  }
  return true;
};

  const formatDate = (date) => {
    const d = new Date(date);
    const options = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return d.toLocaleDateString("en-US", options);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  return (
    <div className="bg-gray-100 shadow-lg mt-4 rounded-lg w-full md:w-2/3 lg:w-3/5 h-80vh mx-auto overflow-y-auto">
      <div className="relative mb-2">
        <div className="h-10 bg-white flex items-center mt-3 justify-center relative">
          <h2 className="text-lg font-semibold">
            {editMode ? "Edit Stock Entry" : "Stock Update"}
          </h2>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-6 pb-4 space-y-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Material</label>
          <div className="bg-gray-50 p-3 rounded-md space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Sub Category:</span>{" "}
                {materialDetails.subcategory}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Specification:</span>{" "}
                {materialDetails.specification}
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">In-Stock Amount:</span>{" "}
              {currentStock} {materialDetails.uom}
            </div>
          </div>
        </div>

        <div className="relative">
          <label className="block text-sm text-gray-600 mb-1">
            Update Date
          </label>
          <div className="bg-gray-50 p-3 rounded-md flex justify-between items-center">
            <div>
              <div className="text-sm font-medium">
                {formatDate(formData.update_date)}
              </div>
            </div>
            <button
              type="button"
              className="flex items-center text-gray-600 text-sm"
              onClick={toggleDatePicker}
            >
              Change
              <Calendar size={16} className="ml-1" />
            </button>
          </div>

          {showDatePicker && (
            <div className="absolute z-10 mt-1 bg-white p-4 rounded-md shadow-lg w-full">
              <label className="block text-sm text-gray-600 mb-2">
                Select Date
              </label>
              <input
                type="date"
                name="update_date"
                value={formData.update_date}
                onChange={handleDateChange}
                className="w-full p-2 border rounded-md"
                max={new Date().toISOString().split("T")[0]}
              />
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  className="bg-gray-200 text-gray-700 px-3 py-1 rounded-md text-sm mr-2"
                  onClick={() => setShowDatePicker(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-purple-600 text-white px-3 py-1 rounded-md text-sm"
                  onClick={() => setShowDatePicker(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex space-x-2">
            <button
              type="button"
              className={`inline-flex items-center px-3 py-1 rounded-md text-sm ${
                stockAction === "added"
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
              onClick={() => setStockAction("added")}
            >
              Added
            </button>
            <button
              type="button"
              className={`inline-flex items-center px-3 py-1 rounded-md text-sm ${
                stockAction === "used"
                  ? "bg-red-500 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
              onClick={() => setStockAction("used")}
            >
              Used
            </button>
          </div>
          <div className="text-xs text-gray-500">
            {stockAction === "added"
              ? "Add stock to inventory"
              : "Remove stock from inventory"}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center">
                <Plus size={16} className="text-gray-400" />
              </div>
              <input
                type="number"
                name="entered_quantity"
                value={formData.entered_quantity}
                onChange={(e) => {
                  if (validateQuantity(e.target.value)) {
                    handleInputChange(e);
                  }
                }}
                placeholder="Enter Quantity"
                className={`w-full px-8 py-2 bg-gray-50 rounded-md ${
                  !validateQuantity(formData.entered_quantity)
                    ? "border-red-500"
                    : ""
                }`}
                min="0"
                step="0.01"
                required
              />
            </div>
            {!validateQuantity(formData.entered_quantity) && (
              <p className="text-red-500 text-xs mt-1">
                Cannot use more than available stock
              </p>
            )}
          </div>
          <div>
            <input
              type="text"
              value={materialDetails.uom}
              disabled
              className="w-full p-2 bg-gray-50 rounded-md"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Remarks/ Comments (Optional)
          </label>
          <textarea
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            placeholder="Type"
            rows="2"
            className="w-full p-2 bg-gray-50 rounded-md resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={
            !formData.entered_quantity ||
            !validateQuantity(formData.entered_quantity) ||
            isLoading
          }
          className="w-full bg-purple-700 text-white py-2 rounded-md hover:bg-purple-800 transition duration-300 flex items-center justify-center mt-4 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <Loader className="h-5 w-5 animate-spin" />
              Processing...
            </div>
          ) : (
            <>
              <span className="mr-2">
                {editMode ? <Save size={18} /> : <Upload size={18} />}
              </span>
              {editMode ? "Save Changes" : "Update Stock"}
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default StockUpdateForm;
